// React Hooks for User Groups
// Data fetching and mutations for user groups

import { useState, useEffect } from 'react';
import { userGroupService } from '../services/userGroupService';

/**
 * Hook to fetch all user groups with optional filters
 */
export function useUserGroups(filters = {}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userGroupService.getAllGroups(filters);
      setGroups(data);
    } catch (err) {
      console.error('Error loading user groups:', err);
      setError(err.message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [JSON.stringify(filters)]);

  const refresh = () => {
    loadGroups();
  };

  return { groups, loading, error, refresh };
}

/**
 * Hook to fetch a single user group by ID
 */
export function useUserGroup(groupId) {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadGroup = async () => {
    if (!groupId) {
      setGroup(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await userGroupService.getGroup(groupId);
      setGroup(data);
    } catch (err) {
      console.error('Error loading user group:', err);
      setError(err.message);
      setGroup(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroup();
  }, [groupId]);

  const refresh = () => {
    loadGroup();
  };

  return { group, loading, error, refresh };
}

/**
 * Hook to fetch groups by department
 */
export function useGroupsByDepartment(departmentId) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadGroups = async () => {
    if (!departmentId) {
      setGroups([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await userGroupService.getGroupsByDepartment(departmentId);
      setGroups(data);
    } catch (err) {
      console.error('Error loading groups by department:', err);
      setError(err.message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [departmentId]);

  const refresh = () => {
    loadGroups();
  };

  return { groups, loading, error, refresh };
}

/**
 * Hook to fetch groups by shift
 */
export function useGroupsByShift(shiftType) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadGroups = async () => {
    if (!shiftType) {
      setGroups([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await userGroupService.getGroupsByShift(shiftType);
      setGroups(data);
    } catch (err) {
      console.error('Error loading groups by shift:', err);
      setError(err.message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [shiftType]);

  const refresh = () => {
    loadGroups();
  };

  return { groups, loading, error, refresh };
}

/**
 * Hook for user group mutations (create, update, delete)
 */
export function useUserGroupMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createGroup = async (groupData, userId) => {
    try {
      setLoading(true);
      setError(null);
      const groupId = await userGroupService.createGroup(groupData, userId);
      return groupId;
    } catch (err) {
      console.error('Error creating group:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateGroup = async (groupId, updates, userId) => {
    try {
      setLoading(true);
      setError(null);
      await userGroupService.updateGroup(groupId, updates, userId);
    } catch (err) {
      console.error('Error updating group:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteGroup = async (groupId, userId) => {
    try {
      setLoading(true);
      setError(null);
      await userGroupService.deleteGroup(groupId, userId);
    } catch (err) {
      console.error('Error deleting group:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addMember = async (groupId, member, userId) => {
    try {
      setLoading(true);
      setError(null);
      await userGroupService.addMember(groupId, member, userId);
    } catch (err) {
      console.error('Error adding member:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (groupId, memberId, userId) => {
    try {
      setLoading(true);
      setError(null);
      await userGroupService.removeMember(groupId, memberId, userId);
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateMember = async (groupId, memberId, memberUpdates, userId) => {
    try {
      setLoading(true);
      setError(null);
      await userGroupService.updateMember(groupId, memberId, memberUpdates, userId);
    } catch (err) {
      console.error('Error updating member:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createGroup,
    updateGroup,
    deleteGroup,
    addMember,
    removeMember,
    updateMember,
    loading,
    error
  };
}

/**
 * Hook to fetch group statistics
 */
export function useGroupStats(groupId) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStats = async () => {
    if (!groupId) {
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await userGroupService.getGroupStats(groupId);
      setStats(data);
    } catch (err) {
      console.error('Error loading group stats:', err);
      setError(err.message);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [groupId]);

  const refresh = () => {
    loadStats();
  };

  return { stats, loading, error, refresh };
}


